// Sidebar — goals/projects tree, collapsed rail, management mode, and
// the ··· More menu. Extracted verbatim from DailyFocusTool.js (refactor
// phase 1); state stays in the container and arrives as props.
import React from 'react';
import { X, ChevronRight, Plus, Archive, RotateCcw, FolderPlus, Target } from 'lucide-react';
import { ThemeSwitcher, PhoIcon } from '../theme';
import { API_BASE } from '../config';

export default function Sidebar({
  zenMode,
  sidebarCollapsed,
  setSidebarCollapsed,
  goals,
  projects,
  actions,
  openFeatureId,
  setOpenFeatureId,
  openGoalId,
  setOpenGoalId,
  expandedGoals,
  filteredGoals,
  filteredProjects,
  getActionCountForProject,
  updateGoal,
  updateProject,
  deleteGoal,
  deleteProject,
  addingProjectForGoal,
  setAddingProjectForGoal,
  inlineProjectName,
  setInlineProjectName,
  addingGoal,
  setAddingGoal,
  inlineGoalName,
  setInlineGoalName,
  unlinkedActions,
  setShowBatchAssignModal,
  setShowProjectImportModal,
  setProjectImportJson,
  setProjectImportError,
  setProjectImportResult,
  copyAiPrompt,
  aiPromptCopied,
  exportBackup,
  loadStarterProject,
  trashCount,
  openTrash,
  openLedgerAt,
  setShowExportModal,
  getAuthHeadersOnly,
  loadData,
  managementMode,
  setManagementMode,
  setEditingProjectName,
  editingProjectName,
  setEditingGoalName,
  editingGoalName,
  setShowCreateModal,
  setCreateModalTab,
  projectPhoFlash,
  goalPhoFlash,
  toggleGoalExpanded,
  addProjectInline,
  addGoalInline,
  displayedActions,
  setSelectedProjectId,
  setNewProjectGoal,
}) {
  // Law 5: no hover-opened panels. One menu is open at a time, named by a
  // string ("goal-3", "proj-8", "settings"); every menu root carries
  // data-menu-root so one document listener can shut whichever is open when
  // the next click lands outside all of them. Esc closes too.
  const [openMenu, setOpenMenu] = React.useState(null);
  // The start-view preference (··· More → "Start on"). Read once, written on
  // change; DailyFocusTool reads the same key at boot.
  const [startViewPref, setStartViewPref] = React.useState(() => {
    try { return localStorage.getItem('phokii-start-view') || 'goals'; } catch { return 'goals'; }
  });
  // ── API tokens (Smartifakt pattern): list, mint-shown-once, revoke ──
  const [tokensOpen, setTokensOpen] = React.useState(false);
  const [tokenList, setTokenList] = React.useState([]);
  const [tokenName, setTokenName] = React.useState('');
  const [minted, setMinted] = React.useState(null);   // { name, token } — shown exactly once
  const [tokenCopied, setTokenCopied] = React.useState(false);
  const loadTokens = async () => {
    try { const r = await fetch(`${API_BASE}/api/tokens`, { headers: getAuthHeadersOnly() }); if (r.ok) setTokenList(await r.json()); } catch {}
  };
  const mintToken = async () => {
    const name = tokenName.trim(); if (!name) return;
    const r = await fetch(`${API_BASE}/api/tokens`, { method: 'POST', headers: { ...getAuthHeadersOnly(), 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) });
    if (r.ok) { setMinted(await r.json()); setTokenName(''); loadTokens(); }
  };
  const revokeToken = async (id) => {
    await fetch(`${API_BASE}/api/tokens/${id}`, { method: 'DELETE', headers: getAuthHeadersOnly() });
    loadTokens();
  };
  React.useEffect(() => {
    if (!tokensOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setMinted(null); setTokensOpen(false); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [tokensOpen]);
  React.useEffect(() => {
    if (!openMenu) return;
    const onDown = (e) => { if (!e.target.closest || !e.target.closest('[data-menu-root]')) setOpenMenu(null); };
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setOpenMenu(null); } };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey, true); };
  }, [openMenu]);
  const menu = (id) => ({
    'data-menu-root': true,
    // A row's ··· is revealed on row-hover (that is fine — it is a control
    // appearing, not a panel opening) but must stay put once its menu is open.
    onClick: (e) => e.stopPropagation(),
  });

  return (
    <>
          {/* Sidebar — hidden entirely in zen writing mode */}
          {zenMode ? null : sidebarCollapsed ? (
            <div className="flex-shrink-0 border-r themed-border flex flex-col items-center py-3 gap-2" style={{width:'48px', minHeight:'calc(100vh - 56px)'}}>
              {goals.filter(i => i.active).map((ini, idx) => {
                const hue = (idx * 47) % 360;
                const hasSelected = projects.find(p => p.goalId === ini.id && p.id === openFeatureId);
                return (
                  <div
                    key={ini.id}
                    className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold cursor-pointer hover:scale-110 transition-transform flex-shrink-0"
                    style={{backgroundColor: `hsl(${hue},55%,50%)`, boxShadow: hasSelected ? `0 0 0 2px hsl(${hue},55%,50%)` : 'none', outline: hasSelected ? '2px solid white' : 'none'}}
                    title={`${ini.name} — open in Goals`}
                    onClick={() => openLedgerAt('goal', ini.id)}
                  >
                    {ini.name.charAt(0).toUpperCase()}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="w-60 flex-shrink-0 border-r themed-border overflow-y-auto bg-gray-50/30" style={{minHeight: 'calc(100vh - 56px)'}}>
              <div className="py-3">

                {/* All Actions */}
                <button
                  onClick={() => { setOpenFeatureId(null); setOpenGoalId(null); }}
                  className={`w-full flex items-center justify-between px-4 py-1.5 text-sm transition-colors duration-100 ${openFeatureId === null ? 'bg-blue-50 text-blue-700 font-semibold' : 'themed-text-secondary hover:themed-surface-hover'}`}
                >
                  <span>All Actions</span>
                  <span className="text-xs themed-text-muted tabular-nums">{displayedActions.length}</span>
                </button>

                <div className="mt-3 space-y-4">
                  {/* Goals with Features */}
                  {filteredGoals.filter(i => i.active).map(goal => {
                    const features = filteredProjects.filter(p => p.goalId === goal.id && p.active);
                    return (
                      // Empty goals recede — full weight returns on hover or once they have projects
                      <div key={goal.id} className={features.length === 0 ? 'opacity-40 hover:opacity-100 transition-opacity' : ''}>
                        {/* Goal row */}
                        <div className="px-4 pb-1 flex items-center gap-1 group/obj">
                          <button
                            onClick={() => {
                              // First click solos the goal's work; a second click opens its card
                              if (openGoalId === goal.id) openLedgerAt('goal', goal.id);
                              else { setOpenGoalId(goal.id); setOpenFeatureId(null); }
                            }}
                            className={`text-[10px] font-semibold uppercase tracking-wider flex-1 leading-tight text-left transition-colors ${openGoalId === goal.id ? 'themed-primary' : 'themed-text-muted hover:themed-text'}`}
                            title={openGoalId === goal.id ? 'Click again to open in Goals — charter, rollup, projects' : 'Show only this goal — click again for its page'}
                          >{goal.name}</button>
                          <div {...menu(`goal-${goal.id}`)} className={`relative transition-opacity ${openMenu === `goal-${goal.id}` ? 'opacity-100' : 'opacity-0 group-hover/obj:opacity-100'}`}>
                            <button onClick={() => setOpenMenu(m => m === `goal-${goal.id}` ? null : `goal-${goal.id}`)}
                              aria-expanded={openMenu === `goal-${goal.id}`} aria-haspopup="menu"
                              className="text-[10px] text-gray-400 hover:text-gray-600 px-1 py-0.5" title="Options">···</button>
                            {openMenu === `goal-${goal.id}` && (
                              <div role="menu" className="absolute right-0 top-5 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-30 min-w-[130px]">
                                <button onClick={() => { setOpenMenu(null); const n = window.prompt('Rename goal:', goal.name); if (n?.trim()) updateGoal(goal.id, { name: n.trim() }); }} className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50">Rename</button>
                                <button onClick={() => { setOpenMenu(null); updateGoal(goal.id, { setAside: !goal.setAside }); }} className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50" title={goal.setAside ? 'Back onto the standing shelf' : 'Rest it at the foot of the shelf — off the standing row, still yours'}>{goal.setAside ? 'Put it back' : 'Set aside'}</button>
                                <button onClick={() => { setOpenMenu(null); if (window.confirm(`Archive "${goal.name}"?`)) updateGoal(goal.id, { active: false }); }} className="w-full text-left px-3 py-1.5 text-xs text-orange-600 hover:bg-orange-50">Archive</button>
                                <button onClick={() => { setOpenMenu(null); deleteGoal(goal.id); }} className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50">Delete</button>
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Project rows */}
                        {features.map(feature => {
                          const count = getActionCountForProject(feature.id);
                          const isSelected = openFeatureId === feature.id;
                          return (
                            <div key={feature.id} className="group/feat flex items-center">
                              <button
                                onClick={() => { if (isSelected) { openLedgerAt('project', feature.id); } else { setOpenFeatureId(feature.id); setOpenGoalId(null); } }}
                                className={`flex-1 flex items-center gap-2.5 px-4 py-1.5 transition-colors duration-100 ${isSelected ? 'bg-blue-50 text-blue-700 font-medium' : 'themed-text-secondary hover:themed-surface-hover'}`}
                              >
                                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{backgroundColor: feature.color || '#94a3b8'}}></span>
                                <span className="flex-1 text-left text-xs leading-tight">{feature.name}</span>
                                <span className="text-xs themed-text-muted tabular-nums">{count}</span>
                              </button>
                              {/* Pho + context menu */}
                              <div className={`relative flex items-center gap-0.5 pr-1 transition-opacity ${openMenu === `proj-${feature.id}` ? 'opacity-100' : 'opacity-0 group-hover/feat:opacity-100'}`}>
                                <button onClick={(e) => { e.stopPropagation(); updateProject(feature.id, { pho: !feature.pho }); }} className={`text-xs px-0.5 ${feature.pho ? 'text-orange-400' : 'text-gray-300 hover:text-orange-400'}`} title="Focus">✦</button>
                                <div {...menu(`proj-${feature.id}`)} className="relative">
                                  <button onClick={() => setOpenMenu(m => m === `proj-${feature.id}` ? null : `proj-${feature.id}`)}
                                    aria-expanded={openMenu === `proj-${feature.id}`} aria-haspopup="menu"
                                    className="text-[10px] text-gray-400 hover:text-gray-600 px-0.5" title="Options">···</button>
                                  {openMenu === `proj-${feature.id}` && (
                                    <div role="menu" className="absolute right-0 top-5 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-30 min-w-[120px]">
                                      <button onClick={() => { setOpenMenu(null); const n = window.prompt('Rename project:', feature.name); if (n?.trim()) updateProject(feature.id, { name: n.trim() }); }} className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50">Rename</button>
                                      <button onClick={() => { setOpenMenu(null); if (window.confirm(`Archive "${feature.name}"?`)) updateProject(feature.id, { active: false }); }} className="w-full text-left px-3 py-1.5 text-xs text-orange-600 hover:bg-orange-50">Archive</button>
                                      <button onClick={async () => { setOpenMenu(null); if (await deleteProject(feature.id)) { if (openFeatureId === feature.id) setOpenFeatureId(null); }}} className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50">Delete</button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        {addingProjectForGoal === goal.id ? (
                          <div className="px-4 py-1 flex items-center gap-1.5">
                            <input
                              autoFocus
                              value={inlineProjectName}
                              onChange={e => setInlineProjectName(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter' && inlineProjectName.trim()) {
                                  addProjectInline(inlineProjectName.trim(), goal.id);
                                  setAddingProjectForGoal(null); setInlineProjectName('');
                                } else if (e.key === 'Escape') {
                                  setAddingProjectForGoal(null); setInlineProjectName('');
                                }
                              }}
                              onBlur={() => { if (!inlineProjectName.trim()) { setAddingProjectForGoal(null); setInlineProjectName(''); } }}
                              placeholder="Project name…"
                              className="flex-1 text-xs px-2 py-1 rounded border themed-border themed-background themed-text focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                            <button onClick={() => { setAddingProjectForGoal(null); setInlineProjectName(''); }} className="themed-text-muted hover:themed-text">
                              <X size={11}/>
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setAddingProjectForGoal(goal.id); setInlineProjectName(''); setAddingGoal(false); }}
                            className="w-full flex items-center gap-1.5 px-4 py-1 text-xs themed-text-muted hover:themed-primary transition-colors"
                          >
                            <Plus size={10}/> Project
                          </button>
                        )}
                      </div>
                    );
                  })}

                  {/* Features with no goal */}
                  {filteredProjects.filter(p => p.active && !p.goalId).map(feature => {
                    const count = getActionCountForProject(feature.id);
                    const isSelected = openFeatureId === feature.id;
                    return (
                      <button
                        key={feature.id}
                        onClick={() => setOpenFeatureId(isSelected ? null : feature.id)}
                        className={`w-full flex items-center gap-2.5 px-4 py-1.5 text-sm transition-colors duration-100 ${isSelected ? 'bg-blue-50 text-blue-700 font-medium' : 'themed-text-secondary hover:themed-surface-hover'}`}
                      >
                        <span className="w-2 h-2 rounded-full flex-shrink-0 bg-gray-400"></span>
                        <span className="flex-1 text-left text-xs leading-tight">{feature.name}</span>
                        <span className="text-xs themed-text-muted tabular-nums">{count}</span>
                      </button>
                    );
                  })}

                  {/* Unassigned */}
                  {displayedActions.filter(g => g.linkType !== 'project').length > 0 && (
                    <button
                      onClick={() => setOpenFeatureId(openFeatureId === 'unassigned' ? null : 'unassigned')}
                      className={`w-full flex items-center gap-2.5 px-4 py-1.5 text-sm transition-colors duration-100 ${openFeatureId === 'unassigned' ? 'bg-blue-50 text-blue-700 font-medium' : 'themed-text-secondary hover:themed-surface-hover'}`}
                    >
                      <span className="w-2 h-2 rounded-full flex-shrink-0 bg-gray-300"></span>
                      <span className="flex-1 text-left text-xs italic">No project</span>
                      <span className="text-xs themed-text-muted tabular-nums">{displayedActions.filter(g => g.linkType !== 'project').length}</span>
                    </button>
                  )}
                </div>

                {/* + Goal */}
                <div className="px-4 mt-5">
                  {addingGoal ? (
                    <div className="flex items-center gap-1.5">
                      <input
                        autoFocus
                        value={inlineGoalName}
                        onChange={e => setInlineGoalName(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && inlineGoalName.trim()) {
                            addGoalInline(inlineGoalName.trim());
                            setAddingGoal(false); setInlineGoalName('');
                          } else if (e.key === 'Escape') {
                            setAddingGoal(false); setInlineGoalName('');
                          }
                        }}
                        onBlur={() => { if (!inlineGoalName.trim()) { setAddingGoal(false); setInlineGoalName(''); } }}
                        placeholder="Goal name…"
                        className="flex-1 text-xs px-2 py-1 rounded border themed-border themed-background themed-text focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                      <button onClick={() => { setAddingGoal(false); setInlineGoalName(''); }} className="themed-text-muted hover:themed-text">
                        <X size={11}/>
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setAddingGoal(true); setInlineGoalName(''); setAddingProjectForGoal(null); }}
                      className="text-xs themed-text-muted hover:themed-primary flex items-center gap-1 transition-colors"
                    >
                      <Plus size={10}/> Goal
                    </button>
                  )}
                </div>

                {/* ··· Settings menu */}
                <div {...menu('settings')} className="px-4 mt-6 pt-4 border-t themed-border-light relative">
                  <button onClick={() => setOpenMenu(m => m === 'settings' ? null : 'settings')}
                    aria-expanded={openMenu === 'settings'} aria-haspopup="menu"
                    className="text-xs themed-text-muted hover:themed-text flex items-center gap-1 transition-colors">
                    ··· More
                  </button>
                  {/* Choosing anything shuts the menu — hovering away used to do
                      that for free. The theme row is exempt: trying three themes
                      in a row is the whole point of it. */}
                  <div
                    onClick={(e) => {
                      if (!e.target.closest) return;
                      if (e.target.closest('[data-menu-keep]')) return;
                      if (e.target.closest('button, a')) setOpenMenu(null);
                    }}
                    className={`absolute left-2 bottom-8 ${openMenu === 'settings' ? 'block' : 'hidden'} bg-white border themed-border rounded-lg shadow-lg py-1 z-20 min-w-[160px]`}
                  >
                    <div data-menu-keep className="px-3 py-1.5 border-b themed-border-light">
                      <ThemeSwitcher />
                    </div>
                    {/* Where the app opens — the user's call, not the flagship's.
                        Takes effect on the next visit; today's view is untouched. */}
                    <div data-menu-keep className="px-3 py-1.5 border-b themed-border-light flex items-center gap-2">
                      <span className="text-[10px] uppercase tracking-widest themed-text-muted">Start on</span>
                      <select
                        value={startViewPref}
                        onChange={e => {
                          setStartViewPref(e.target.value);
                          try { localStorage.setItem('phokii-start-view', e.target.value); } catch {}
                        }}
                        className="text-xs themed-text bg-transparent border-0 focus:outline-none cursor-pointer flex-1"
                        title="Which view the app opens on next time"
                      >
                        <option value="goals">Goals</option>
                        <option value="list">List</option>
                        <option value="board">Board</option>
                        <option value="calendar">Calendar</option>
                      </select>
                    </div>
                    {unlinkedActions.length > 0 && (
                      <button onClick={() => setShowBatchAssignModal(true)} className="w-full text-left px-3 py-1.5 text-xs themed-text hover:bg-gray-50">
                        Assign {unlinkedActions.length} unlinked
                      </button>
                    )}
                    <button onClick={() => { setShowProjectImportModal(true); setProjectImportJson(''); setProjectImportError(''); setProjectImportResult(null); }} className="w-full text-left px-3 py-1.5 text-xs themed-text hover:bg-gray-50">
                      Import projects (JSON)
                    </button>
                    <button onClick={copyAiPrompt} className="w-full text-left px-3 py-1.5 text-xs themed-text hover:bg-gray-50" title="Copies a prompt that teaches ChatGPT/Claude/Gemini to plan in Phōkii's import format">
                      {aiPromptCopied ? 'AI prompt copied ✓' : 'Copy AI prompt'}
                    </button>
                    <button onClick={() => setShowExportModal(true)} className="w-full text-left px-3 py-1.5 text-xs themed-text hover:bg-gray-50">
                      Export CSV
                    </button>
                    <button onClick={exportBackup} className="w-full text-left px-3 py-1.5 text-xs themed-text hover:bg-gray-50" title="Download your entire workspace — goals, projects, actions, notes, milestones — as one JSON file">
                      Export backup (JSON)
                    </button>
                    <button onClick={loadStarterProject} className="w-full text-left px-3 py-1.5 text-xs themed-text hover:bg-gray-50" title="Adds “How Phōkii works — the manual” to your shelf as a goal you can read and then delete (additive — touches nothing else)">
                      Load the manual
                    </button>
                    <button onClick={() => { setTokensOpen(true); setMinted(null); loadTokens(); }} className="w-full text-left px-3 py-1.5 text-xs themed-text hover:bg-gray-50" title="Personal access tokens — let your own Claude or scripts read your shelf and plant plans. Additive only: a token can never overwrite or delete anything.">
                      API tokens…
                    </button>
                    <button onClick={openTrash} className="w-full text-left px-3 py-1.5 text-xs themed-text hover:bg-gray-50" title="Deletes rest here for 30 days — restore or purge">
                      Recently deleted{trashCount > 0 ? ` (${trashCount})` : ''}
                    </button>
                    <a
                      href="mailto:audiopablo@gmail.com?subject=Ph%C5%8Dkii%20feedback"
                      className="block w-full text-left px-3 py-1.5 text-xs themed-text hover:bg-gray-50"
                      title="Beta feedback goes straight to Paul — screenshots welcome"
                    >
                      Send feedback
                    </a>
                    <button
                      onClick={async () => {
                        if (window.confirm("Archive completed actions and reset score?")) {
                          try {
                            const r = await fetch(`${API_BASE}/api/reset`, { method: 'POST', headers: getAuthHeadersOnly() });
                            if (!r.ok) throw new Error('Reset failed');
                            await loadData();
                          } catch (err) { alert("Failed: " + err.message); }
                        }
                      }}
                      className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50"
                    >
                      Reset Scoring
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* DEAD CODE — old sidebar content below, kept for reference */}
          {false && (
            <div className="space-y-6 opacity-85 min-w-0">
              
              
              {/* Daily Stats */}

              {/* Strategic Hierarchy */}
              <div className="themed-background border themed-border rounded-xl shadow-sm min-w-[320px]">
                <div className="p-4 border-b themed-border-light themed-surface rounded-t-xl">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-medium themed-text flex items-center">
                      <Target className="mr-2" size={18} />
                      Goals
                    </h2>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setManagementMode(!managementMode)}
                        className={`px-3 py-1.5 text-sm rounded-lg transition-all duration-200 ${
                          managementMode 
                            ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                            : 'themed-surface-hover themed-text-secondary'
                        }`}
                        title={managementMode ? 'Exit management mode' : 'Enter management mode'}
                      >
                        {managementMode ? '✓ Done' : 'Manage'}
                      </button>
                      <button
                        onClick={() => {
                          setCreateModalTab('action');
                          setShowCreateModal(true);
                        }}
                        className="p-2 themed-primary themed-surface-hover rounded-lg transition-all duration-200 flex items-center space-x-1"
                        title="Add new goal, project, or action"
                      >
                        <Plus size={16} />
                        <span className="text-sm font-medium">Add</span>
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 space-y-4">
                  {/* Active Goals with Projects */}
                  {filteredGoals.filter(i => i.active).map((goal, goalIndex) => {
                    const goalProjects = filteredProjects.filter(p => p.goalId === goal.id && p.active);
                    const isExpanded = expandedGoals.has(goal.id);
                    const totalActions = goalProjects.reduce((sum, p) => sum + getActionCountForProject(p.id), 0);
                    const isFlashing = goalPhoFlash.has(goal.id);
                    
                    return (
                      <div key={goal.id} className="border themed-border-light rounded-lg overflow-hidden">
                        {/* Goal Header */}
                        <div 
                          className={`flex items-center justify-between p-3 cursor-pointer transition-colors duration-200 group ${
                            goal.pho ? 'themed-pho-light border-l-4 themed-pho-border' : 'themed-surface hover:themed-surface-hover'
                          } ${isFlashing ? 'animate-pulse ring-2 ring-orange-300 ring-opacity-50' : ''}`}
                          onClick={() => {
                            setNewProjectGoal(goal.id.toString());
                            setCreateModalTab('project');
                            setShowCreateModal(true);
                          }}
                        >
                          <div className="flex items-start space-x-3 flex-1 min-w-0">
                            <ChevronRight 
                              className={`transform transition-transform duration-200 flex-shrink-0 mt-0.5 ${isExpanded ? 'rotate-90' : ''}`} 
                              size={14}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleGoalExpanded(goal.id);
                              }}
                            />
                            <div className="text-sm font-medium themed-text break-words leading-tight flex-1 min-w-0">
                              {editingGoalName === goal.id ? (
                                <input
                                  type="text"
                                  defaultValue={goal.name}
                                  className="w-full p-1 text-sm border themed-border rounded focus:ring-2 focus:ring-blue-300 focus:border-transparent themed-background themed-text"
                                  autoFocus
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      updateGoal(goal.id, { name: e.target.value });
                                      setEditingGoalName(null);
                                    } else if (e.key === 'Escape') {
                                      setEditingGoalName(null);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    updateGoal(goal.id, { name: e.target.value });
                                    setEditingGoalName(null);
                                  }}
                                />
                              ) : (
                                goal.name
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                            {/* Only show Pho button on hover */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                updateGoal(goal.id, { pho: !goal.pho });
                              }}
                              className="p-2 rounded-full transition-all duration-200 opacity-0 group-hover:opacity-100 themed-text-muted hover:themed-text-secondary hover:bg-gray-100 dark:hover:bg-gray-700 hover:shadow-md"
                              title={goal.pho ? "Unmark Pho" : "Mark Pho"}
                              aria-pressed={goal.pho}
                            >
                              <PhoIcon active={goal.pho} />
                            </button>
                          </div>
                          <div className="flex items-center space-x-2">
                            {managementMode && (
                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingGoalName(goal.id);
                                  }}
                                  className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-all duration-200"
                                  title="Edit goal name"
                                >
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                  </svg>
                                </button>
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const hasChildProjects = projects.some(p => p.goalId === goal.id);
                                    if (hasChildProjects) {
                                      alert('Cannot archive goal with active projects. Please remove or move the projects first.');
                                    } else {
                                      updateGoal(goal.id, { active: false });
                                    }
                                  }}
                                  className="p-1 text-orange-500 hover:text-orange-700 hover:bg-orange-50 rounded transition-all duration-200"
                                  title="Archive goal"
                                >
                                  <Archive size={12} />
                                </button>
                                <button 
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    const hasChildProjects = projects.some(p => p.goalId === goal.id);
                                    if (hasChildProjects) {
                                      alert('Cannot delete goal with active projects. Please remove or move the projects first.');
                                    } else {
                                      await deleteGoal(goal.id);
                                    }
                                  }}
                                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-all duration-200"
                                  title="Delete goal"
                                >
                                  <X size={12} />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Projects under this Goal */}
                        {isExpanded && (
                          <div className="border-t themed-border-light">
                            {goalProjects.length > 0 ? (
                              <div className="space-y-2 p-3">
                                {goalProjects.map((project) => {
                                  // const projectActionCount = getActionCountForProject(project.id);
                                  const isFlashing = projectPhoFlash.has(project.id);
                                  return (
                                    <div
                                      key={project.id}
                                      onClick={() => {
                                        setCreateModalTab('action');
                                        setShowCreateModal(true);
                                        setSelectedProjectId(project.id.toString());
                                      }}
                                      className={`flex items-center justify-between p-2 rounded border transition-all duration-200 cursor-pointer ${
                                        project.pho ? 'themed-pho-light themed-pho-border shadow-sm' : 'themed-background themed-border-light hover:shadow-sm'
                                      } group ${isFlashing ? 'animate-pulse ring-2 ring-orange-300 ring-opacity-50' : ''}`}
                                    >
                                      <div className="flex items-start space-x-3 flex-1 min-w-0">
                                        <div className="w-2 h-8 themed-text-muted rounded-full flex-shrink-0"></div>
                                        <div className="text-sm themed-text-secondary break-words leading-tight flex-1">
                                          {editingProjectName === project.id ? (
                                            <input
                                              type="text"
                                              defaultValue={project.name}
                                              className="w-full p-1 text-sm border themed-border rounded focus:ring-2 focus:ring-blue-300 focus:border-transparent themed-background themed-text"
                                              autoFocus
                                              onClick={(e) => e.stopPropagation()}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                  updateProject(project.id, { name: e.target.value });
                                                  setEditingProjectName(null);
                                                } else if (e.key === 'Escape') {
                                                  setEditingProjectName(null);
                                                }
                                              }}
                                              onBlur={(e) => {
                                                updateProject(project.id, { name: e.target.value });
                                                setEditingProjectName(null);
                                              }}
                                            />
                                          ) : (
                                            `${project.name} (${getActionCountForProject(project.id)})`
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                                        {/* Rename and Delete buttons */}
                                        {/* Only show Pho button on hover */}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            updateProject(project.id, { pho: !project.pho });
                                          }}
                                          className="p-2 rounded-full transition-all duration-200 opacity-0 group-hover:opacity-100 themed-text-muted hover:themed-text-secondary hover:bg-gray-100 dark:hover:bg-gray-700 hover:shadow-md"
                                          title={project.pho ? "Unmark Pho" : "Mark Pho"}
                                          aria-pressed={project.pho}
                                        >
                                          <PhoIcon active={project.pho} />
                                        </button>
                                        {managementMode && (
                                          <div className="flex items-center space-x-1">
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setEditingProjectName(project.id);
                                              }}
                                              className="p-1 rounded text-blue-500 hover:text-blue-700 hover:bg-blue-50 transition-all duration-200"
                                              title="Edit project name"
                                            >
                                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                              </svg>
                                            </button>
                                            <button 
                                              onClick={() => updateProject(project.id, { active: false })}
                                              className="p-1 rounded text-orange-500 hover:text-orange-700 hover:bg-orange-50 transition-all duration-200"
                                              title="Archive project"
                                            >
                                              <Archive size={12} />
                                            </button>
                                            <button 
                                              onClick={async () => {
                                                const hasActions = actions.some(g => g.linkType === 'project' && g.linkId === project.id);
                                                if (hasActions) {
                                                  alert('Cannot delete project with active actions. Please reassign or delete the actions first.');
                                                } else {
                                                  await deleteProject(project.id);
                                                }
                                              }}
                                              className="p-1 rounded text-red-500 hover:text-red-700 hover:bg-red-50 transition-all duration-200"
                                              title="Delete project"
                                            >
                                              <X size={12} />
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="p-3 text-xs themed-text-muted italic">No projects in this goal</div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  {/* Unlinked Projects */}
                  {filteredProjects.filter(p => p.goalId === null && p.active).length > 0 && (
                    <div className="border themed-border-light rounded-lg overflow-hidden">
                      <div className="flex items-center justify-between p-3 themed-primary-bg text-white">
                        <div className="flex items-center space-x-3">
                          <FolderPlus size={14} />
                          <span className="text-sm font-medium">Unlinked Projects</span>
                        </div>
                      </div>
                      <div className="space-y-2 p-3">
                        {filteredProjects.filter(p => p.goalId === null && p.active).map((project) => {
                          // const projectActionCount = getActionCountForProject(project.id);
                          const isFlashing = projectPhoFlash.has(project.id);
                          return (
                            <div
                              key={project.id}
                              onClick={() => {
                                setCreateModalTab('action');
                                setShowCreateModal(true);
                                setSelectedProjectId(project.id.toString());
                              }}
                              className={`flex items-center justify-between p-2 rounded border transition-all duration-200 cursor-pointer ${
                                project.pho ? 'themed-pho-light themed-pho-border shadow-sm' : 'themed-background themed-border-light hover:shadow-sm'
                              } group ${isFlashing ? 'animate-pulse ring-2 ring-orange-300 ring-opacity-50' : ''}`}
                            >
                              <div className="flex items-start space-x-3 flex-1 min-w-0">
                                <div className="w-2 h-8 themed-primary-bg rounded-full flex-shrink-0"></div>
                                <div className="text-sm themed-text-secondary break-words leading-tight flex-1">
                                  {editingProjectName === project.id ? (
                                    <input
                                      type="text"
                                      defaultValue={project.name}
                                      className="w-full p-1 text-sm border themed-border rounded focus:ring-2 focus:ring-blue-300 focus:border-transparent themed-background themed-text"
                                      autoFocus
                                      onClick={(e) => e.stopPropagation()}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          updateProject(project.id, { name: e.target.value });
                                          setEditingProjectName(null);
                                        } else if (e.key === 'Escape') {
                                          setEditingProjectName(null);
                                        }
                                      }}
                                      onBlur={(e) => {
                                        updateProject(project.id, { name: e.target.value });
                                        setEditingProjectName(null);
                                      }}
                                    />
                                  ) : (
                                    `${project.name} (${getActionCountForProject(project.id)})`
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                                {/* Rename and Delete buttons - always visible on hover */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingProjectName(project.id);
                                  }}
                                  className="p-1.5 transition-opacity duration-200 opacity-0 group-hover:opacity-100 themed-text-muted hover:text-blue-600 hover:bg-blue-50 rounded"
                                  title="Rename project"
                                >
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                  </svg>
                                </button>
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    await deleteProject(project.id);
                                  }}
                                  className="p-1.5 transition-opacity duration-200 opacity-0 group-hover:opacity-100 themed-text-muted hover:text-red-600 hover:bg-red-50 rounded"
                                  title="Delete project"
                                >
                                  <X size={12}/>
                                </button>
                                {/* Pho icon moved to end, ml-auto */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateProject(project.id, { pho: !project.pho });
                                  }}
                                  className="p-2 rounded-full transition-all duration-200 opacity-0 group-hover:opacity-100 themed-text-muted hover:themed-text-secondary hover:bg-gray-100 dark:hover:bg-gray-700 hover:shadow-md"
                                  title={project.pho ? "Unmark Pho" : "Mark Pho"}
                                  aria-pressed={project.pho}
                                >
                                  <PhoIcon active={project.pho} />
                                </button>
                                {managementMode && (
                                  <div className="flex items-center space-x-1">
                                    <button 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        updateProject(project.id, { active: false });
                                      }}
                                      className="p-1 rounded text-orange-500 hover:text-orange-700 hover:bg-orange-50 transition-all duration-200"
                                      title="Archive project"
                                    >
                                      <Archive size={12} />
                                    </button>
                                    <button 
                                      onClick={async () => {
                                        const hasActions = actions.some(g => g.linkType === 'project' && g.linkId === project.id);
                                        if (hasActions) {
                                          alert('Cannot delete project with active actions. Please reassign or delete the actions first.');
                                        } else {
                                          await deleteProject(project.id);
                                        }
                                      }}
                                      className="p-1 rounded text-red-500 hover:text-red-700 hover:bg-red-50 transition-all duration-200"
                                      title="Delete project"
                                    >
                                      <X size={12} />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
      {/* Create Modal JSX (snippet for "Create another" checkbox) */}
                      </div>
                    </div>
                  )}

                  {/* Archived Goals */}
                  {goals.filter(i => !i.active).map((goal) => (
                    <div 
                      key={goal.id} 
                      className="flex items-center justify-between themed-surface p-3 rounded border themed-border opacity-60"
                    >
                      <div className="flex items-center space-x-3 min-w-0 flex-1">
                        <div className="w-3 h-3 rounded-full themed-text-muted flex-shrink-0"></div>
                        <span className="text-sm themed-text-muted truncate">{goal.name} [Archived]</span>
                      </div>
                      {managementMode && (
                        <div className="flex items-center space-x-1 flex-shrink-0 ml-2">
                          <button 
                            onClick={() => updateGoal(goal.id, { active: true })}
                            className="p-1 text-green-500 hover:text-green-700 hover:bg-green-50 rounded transition-all duration-200"
                            title="Reactivate goal"
                          >
                            <RotateCcw size={12} />
                          </button>
                          <button 
                            onClick={() => deleteGoal(goal.id)}
                            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-all duration-200"
                            title="Delete goal"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Archived Projects */}
                  {projects.filter(p => !p.active).map((project) => (
                    <div key={project.id} className="themed-surface p-3 rounded-lg border themed-border opacity-60">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 min-w-0 flex-1">
                          <div className="w-3 h-3 rounded-full flex-shrink-0 themed-text-muted"></div>
                          <span className="themed-text-muted text-sm truncate">
                            {project.name} [Archived]
                          </span>
                        </div>
                        {managementMode && (
                          <div className="flex items-center space-x-1 flex-shrink-0 ml-2">
                            <button 
                              onClick={() => updateProject(project.id, { active: true })}
                              className="p-1 rounded text-green-500 hover:text-green-700 hover:bg-green-50 transition-all duration-200"
                              title="Reactivate project"
                            >
                              <RotateCcw size={12} />
                            </button>
                            <button 
                              onClick={async () => { await deleteProject(project.id); }}
                              className="p-1 rounded text-red-500 hover:text-red-700 hover:bg-red-50 transition-all duration-200"
                              title="Delete project"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}


    {tokensOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,.35)' }} onClick={() => { setMinted(null); setTokensOpen(false); }}>
        <div className="themed-background rounded-xl shadow-2xl border themed-border w-[460px] max-w-[92vw] p-5 text-left" onClick={e => e.stopPropagation()}>
          <div className="flex items-baseline justify-between mb-1">
            <h3 className="text-[15px] font-semibold themed-text">API tokens</h3>
            <button onClick={() => { setMinted(null); setTokensOpen(false); }} className="text-xs themed-text-muted hover:themed-text">esc</button>
          </div>
          <p className="text-[11.5px] themed-text-muted mb-4" style={{ maxWidth: '46ch' }}>
            A token lets your own Claude or scripts read your shelf and plant goals, projects and actions.
            Tokens are additive by design — one can never overwrite, delete, or manage your account.
          </p>

          {minted ? (
            <div className="border themed-border rounded-lg p-3 mb-4">
              <p className="text-[11px] uppercase tracking-widest themed-text-muted font-semibold mb-1.5">“{minted.name}” — copy it now</p>
              <code className="block text-[11px] themed-text break-all mb-2">{minted.token}</code>
              <div className="flex items-center gap-3">
                <button
                  onClick={async () => { try { await navigator.clipboard.writeText(minted.token); setTokenCopied(true); setTimeout(() => setTokenCopied(false), 1800); } catch {} }}
                  className="px-3 py-1 text-xs themed-button-primary rounded-lg">{tokenCopied ? 'Copied ✓' : 'Copy token'}</button>
                <span className="text-[11px] themed-text-muted">It will never be shown again.</span>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 mb-4">
              <input value={tokenName} onChange={e => setTokenName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') mintToken(); e.stopPropagation(); }}
                placeholder="Name it — e.g. Claude Code on my Mac"
                className="flex-1 px-3 py-1.5 text-xs border themed-border rounded-lg themed-background themed-text focus:outline-none" />
              <button onClick={mintToken} disabled={!tokenName.trim()} className="px-3 py-1.5 text-xs themed-button-primary rounded-lg disabled:opacity-40">New token</button>
            </div>
          )}

          {tokenList.length > 0 && (
            <div className="border-t themed-border-light pt-2">
              {tokenList.map(t => (
                <div key={t.id} className="flex items-baseline gap-3 py-1.5 border-b themed-border-light">
                  <span className="text-[12px] themed-text flex-1 min-w-0 truncate">{t.name}</span>
                  <code className="text-[10.5px] themed-text-muted">{t.prefix}…</code>
                  <button onClick={() => revokeToken(t.id)} className="text-[11px] themed-text-muted hover:text-red-600">revoke</button>
                </div>
              ))}
            </div>
          )}
          {tokenList.length === 0 && !minted && (
            <p className="text-[11.5px] themed-text-muted italic">No tokens yet.</p>
          )}
        </div>
      </div>
    )}
    </>
  );
}
